// src/pages/Admin/VotePage/utils/voteAdmin.utils.js

import dayjs from "dayjs";

export const cx = (...classes) =>
  classes.filter(Boolean).join(" ");

export const parseBoolean = (
  value,
  fallback = false,
) => {
  if (
    value === undefined ||
    value === null ||
    value === ""
  ) {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value === "number") {
    return value === 1;
  }

  const normalizedValue = String(value)
    .trim()
    .toLowerCase();

  if (
    ["true", "1", "yes", "oui"].includes(
      normalizedValue,
    )
  ) {
    return true;
  }

  if (
    ["false", "0", "no", "non"].includes(
      normalizedValue,
    )
  ) {
    return false;
  }

  return fallback;
};

export const parseIdArray = (value) => {
  try {
    if (value == null) {
      return [];
    }

    let parsedValue = value;

    if (typeof value === "string") {
      parsedValue = JSON.parse(value);
    }

    if (!Array.isArray(parsedValue)) {
      return [];
    }

    return [
      ...new Set(
        parsedValue
          .map((item) => {
            if (
              typeof item === "object" &&
              item !== null
            ) {
              return (
                item.id ??
                item.role_id ??
                item.group_id ??
                item.actor_group_id ??
                null
              );
            }

            return item;
          })
          .map(Number)
          .filter(Number.isFinite),
      ),
    ];
  } catch {
    return [];
  }
};

export const computeVoteStatus = (vote) => {
  if (
    !parseBoolean(
      vote?.is_active,
      true,
    )
  ) {
    return "inactif";
  }

  const now = dayjs();
  const start = dayjs(vote?.start_time);
  const end = dayjs(vote?.end_time);

  if (
    start.isValid() &&
    now.isBefore(start)
  ) {
    return "a_venir";
  }

  if (
    end.isValid() &&
    now.isAfter(
      end.endOf("day"),
    )
  ) {
    return "termine";
  }

  return "actif";
};

export const normalizeVote = (vote) => {
  const useClubWeight =
    parseBoolean(
      vote?.use_club_weight,
      false,
    );

  const oneVotePerClub =
    useClubWeight ||
    parseBoolean(
      vote?.one_vote_per_club,
      false,
    );

  return {
    ...vote,

    is_active:
      parseBoolean(
        vote?.is_active,
        true,
      ),

    is_anonymous:
      parseBoolean(
        vote?.is_anonymous,
        true,
      ),

    use_club_weight:
      useClubWeight,

    one_vote_per_club:
      oneVotePerClub,

    _status:
      computeVoteStatus(vote),

    _allowedRoleIds:
      parseIdArray(
        vote?.allowed_roles,
      ),

    _allowedActorGroupIds:
      parseIdArray(
        vote?.allowed_actor_group_ids ??
          vote?.allowed_group_ids ??
          vote?.actor_group_ids,
      ),
  };
};

export const formatVoteDate = (value) => {
  if (
    !value ||
    !dayjs(value).isValid()
  ) {
    return "Date non renseignée";
  }

  return dayjs(value).format(
    "DD/MM/YYYY",
  );
};

export const formatVotePeriod = (vote) => {
  const start = formatVoteDate(
    vote?.start_time,
  );

  const end = formatVoteDate(
    vote?.end_time,
  );

  return `${start} → ${end}`;
};

export const toDateInputValue = (value) => {
  if (
    !value ||
    !dayjs(value).isValid()
  ) {
    return "";
  }

  return dayjs(value).format(
    "YYYY-MM-DD",
  );
};

export const getVoteStatistics = (
  votes = [],
) => {
  const normalizedVotes =
    votes.map(normalizeVote);

  return {
    total:
      normalizedVotes.length,

    active:
      normalizedVotes.filter(
        (vote) =>
          vote._status ===
          "actif",
      ).length,

    upcoming:
      normalizedVotes.filter(
        (vote) =>
          vote._status ===
          "a_venir",
      ).length,

    voters:
      normalizedVotes.reduce(
        (total, vote) =>
          total +
          Number(
            vote?.voterCount ||
              0,
          ),
        0,
      ),

    questions:
      normalizedVotes.reduce(
        (total, vote) =>
          total +
          Number(
            vote?.questionCount ||
              0,
          ),
        0,
      ),

    clubVotes:
      normalizedVotes.filter(
        (vote) =>
          vote.one_vote_per_club,
      ).length,

    weightedVotes:
      normalizedVotes.filter(
        (vote) =>
          vote.use_club_weight,
      ).length,
  };
};

export const buildVotePayload = ({
  formData,
  roleIds,
  actorGroupIds,
}) => {
  const useClubWeight =
    Boolean(
      formData.use_club_weight,
    );

  /*
   * La pondération impose automatiquement
   * un seul vote par club.
   */
  const oneVotePerClub =
    useClubWeight ||
    Boolean(
      formData.one_vote_per_club,
    );

  return {
    title:
      formData.title.trim(),

    description:
      formData.description.trim(),

    start_time:
      formData.start_time,

    end_time:
      formData.end_time,

    type:
      formData.type,

    is_active:
      Boolean(
        formData.is_active,
      ),

    is_anonymous:
      Boolean(
        formData.is_anonymous,
      ),

    one_vote_per_club:
      oneVotePerClub,

    use_club_weight:
      useClubWeight,

    allowed_roles:
      roleIds.map(Number),

    allowed_actor_group_ids:
      actorGroupIds.map(Number),
  };
};

export const getActorGroupLabel = (group) =>
  group?.label ||
  group?.name ||
  group?.nom ||
  group?.title ||
  `Groupe #${group?.id}`;

export const getActorGroupMemberCount = (
  group,
) =>
  Number(
    group?.nombreActeurs ??
      group?.actorCount ??
      group?.actors_count ??
      group?.acteurCount ??
      group?.acteurs_count ??
      group?.members_count ??
      0,
  );